import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import i18n, {
  DEFAULT_LANGUAGE,
  detectBrowserLanguage,
  getStoredLanguage,
  normalizeLanguage,
  setStoredLanguage
} from "../../i18n";
import { setUserData } from "../redux/auth/authSlice";
import { getCurrentUser } from "../services/company/userService";

function syncStoredAuthLanguage(language, dispatch, currentUser) {
  const normalizedLanguage = normalizeLanguage(language);

  try {
    const raw = window.localStorage.getItem("auth_user");
    if (!raw) {
      return normalizedLanguage;
    }

    const parsed = JSON.parse(raw);
    const storedUser = parsed?.user || {};
    const nextUser = {
      ...(storedUser || currentUser || {}),
      language: normalizedLanguage
    };

    if (
      storedUser?.language === normalizedLanguage &&
      currentUser?.language === normalizedLanguage
    ) {
      return normalizedLanguage;
    }

    window.localStorage.setItem(
      "auth_user",
      JSON.stringify({
        ...parsed,
        user: nextUser
      })
    );

    dispatch(setUserData({ user: nextUser }));
  } catch (error) {
    console.error("Failed to sync stored user language:", error);
  }

  return normalizedLanguage;
}

export default function LanguageBootstrap() {
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth?.accessToken);
  const currentUser = useSelector((state) => state.auth?.user);
  const currentUserRef = useRef(currentUser);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    let isActive = true;

    const initializeLanguage = async () => {
      let resolvedLanguage = DEFAULT_LANGUAGE;

      if (accessToken) {
        try {
          const user = await getCurrentUser();
          if (user?.language) {
            resolvedLanguage = normalizeLanguage(user.language);
            if (!isActive) {
              return;
            }

            syncStoredAuthLanguage(resolvedLanguage, dispatch, user);
          }
        } catch (error) {
          console.error("Failed to load user language preference:", error);
        }
      }

      if (resolvedLanguage === DEFAULT_LANGUAGE && !currentUserRef.current?.language) {
        resolvedLanguage =
          getStoredLanguage() || detectBrowserLanguage() || DEFAULT_LANGUAGE;
      }

      if (!isActive) {
        return;
      }

      const languageToApply = setStoredLanguage(resolvedLanguage);
      await i18n.changeLanguage(languageToApply);

      if (accessToken && currentUserRef.current) {
        syncStoredAuthLanguage(languageToApply, dispatch, currentUserRef.current);
      }
    };

    console.debug("[api-debug] LanguageBootstrap initializeLanguage", {
      hasAccessToken: Boolean(accessToken),
      currentLanguage: currentUserRef.current?.language
    });
    void initializeLanguage();

    return () => {
      isActive = false;
    };
  }, [accessToken, dispatch]);

  return null;
}
